package org.bsvac.bsvac_kmp

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.ui.NavDisplay
import org.bsvac.bsvac_kmp.data.BsvacTextStyle
import org.bsvac.bsvac_kmp.data.models.ContentSectionId
import org.bsvac.bsvac_kmp.data.fakeData.DataSource
import org.bsvac.bsvac_kmp.data.models.AgencyModel
import org.bsvac.bsvac_kmp.data.models.AmbulanceUnitModel
import org.bsvac.bsvac_kmp.data.models.BoroughModel
import org.bsvac.bsvac_kmp.data.models.CrewModel
import org.bsvac.bsvac_kmp.data.models.RadioModel
import org.bsvac.bsvac_kmp.data.models.TourModel
import org.bsvac.bsvac_kmp.ui.BottomRow
import org.bsvac.bsvac_kmp.ui.ContentSection
import org.bsvac.bsvac_kmp.ui.MiddleRow
import org.bsvac.bsvac_kmp.ui.StatusRow
import org.bsvac.bsvac_kmp.ui.SubScreenDisplay
import org.bsvac.bsvac_kmp.ui.SubScreens
import org.bsvac.bsvac_kmp.ui.TopRow
import org.bsvac.bsvac_kmp.ui.models.Choices
import org.bsvac.bsvac_kmp.ui.subscreens.DevSettings
import org.bsvac.bsvac_kmp.ui.models.LogonSubScreenModel
import org.bsvac.bsvac_kmp.ui.models.OnSelection
import org.bsvac.bsvac_kmp.ui.models.Selected
import org.jetbrains.compose.ui.tooling.preview.Preview
import org.jetbrains.compose.ui.tooling.preview.PreviewParameter
import org.jetbrains.compose.ui.tooling.preview.PreviewParameterProvider


@Composable
fun App(
    viewModel: MainViewModel = viewModel { MainViewModel() }
) {
    val uiState = viewModel.uiState.collectAsStateWithLifecycle()
    val subscreenDisplayed = viewModel.subscreenState.collectAsStateWithLifecycle()
    val errorMessage = viewModel.errorMessage.collectAsStateWithLifecycle()
    val time = viewModel.timeFlow.collectAsStateWithLifecycle()
    val selection = viewModel.selectedState.collectAsStateWithLifecycle()
    val bsvacTextStyle = viewModel.textStyle.collectAsStateWithLifecycle()
    val backStack = remember { mutableStateListOf<SubScreenDisplay>() }
    val optionSet = viewModel.logonOptionSet.collectAsStateWithLifecycle()
    val logonSubScreenModel = LogonSubScreenModel(
        bsvacTextStyle = bsvacTextStyle.value,
        onSelection = OnSelection(
            onAgencySelected = viewModel::onAgencySelected,
            onCrewMemberSelected = viewModel::onCrewMemberSelected,
            onBoroughSelected = viewModel::onBoroughSelected,
            onAmbulanceUnitSelected = viewModel::onAmbulanceUnitSelected,
            onTourSelected = viewModel::onTourSelected,
            onRadioSelected = viewModel::onRadioSelected,
        ),
        choices = Choices(
            agencyOptions = AgencyModel.entries,
            boroughOptions = optionSet.value.boroughOptions,
            crewMemberOptions = optionSet.value.crewOptions,
            unitOptions = optionSet.value.unitOptions,
            tourOptions = optionSet.value.tourOptions,
            radioOptions = optionSet.value.radioOptions,
        ),
        selected = selection.value

    )
    val root = SubScreenDisplay(
        sectionId = subscreenDisplayed.value,
        display = SubScreens.content(
            modifier = Modifier,
            displayError = errorMessage.value,
            sectionId = subscreenDisplayed.value,
            errorDisplayed = viewModel::errorDisplayed,
            logonSubScreenModel = logonSubScreenModel
        )
    )
    backStack.add(root)
    NavDisplay(
        backStack = backStack,
        onBack = { backStack.removeLastOrNull() },
        entryProvider = entryProvider{
            entry<SubScreenDisplay> {
                val currentBsvacTextStyle = bsvacTextStyle.value
                AppContent(
                    mainUiState = uiState.value,
                    onNavigationClicked = viewModel::onNavigationClicked,
                    onActionClicked = viewModel::onActionClicked,
                    subscreenDisplayed = subscreenDisplayed.value,
                    time = time.value,
                    content = it.display,
                    bsvacTextStyle = currentBsvacTextStyle,
                    currentFontSize = currentBsvacTextStyle.fontSize,
                    onFontSizeUpdated = viewModel::setFontSize,
                    topRowSectionIds = ContentSectionId.topRowIds(),
                    middleColumnSectionIds = ContentSectionId.middleColumnIds(),
                    bottomRowSectionIds = ContentSectionId.bottomRowIds()
                )
            }
        }
    )

}

@Composable
fun AppContent(
    mainUiState: MainUiState,
    onNavigationClicked: (ContentSectionId) -> Unit,
    onActionClicked: (ContentSectionId) -> Unit,
    subscreenDisplayed: ContentSectionId,
    time: String,
    currentFontSize: TextUnit,
    onFontSizeUpdated: (TextUnit) -> Unit,
    content: @Composable (BoxScope.() -> Unit),
    bsvacTextStyle: BsvacTextStyle,
    topRowSectionIds: List<ContentSectionId>,
    middleColumnSectionIds: List<ContentSectionId>,
    bottomRowSectionIds: List<ContentSectionId>
) {
    MaterialTheme {
        Box {
            Column(
                modifier = Modifier
                    .background(MaterialTheme.colorScheme.primaryContainer)
                    .safeContentPadding()
                    .fillMaxSize(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                TopRow(
                    scope = this,
                    mainUiState = mainUiState,
                    navigationClicked = onNavigationClicked,
                    actionClicked = onActionClicked,
                    bsvacTextStyle = bsvacTextStyle,
                    sectionIds = topRowSectionIds
                )
                MiddleRow(
                    scope = this,
                    mainUiState = mainUiState,
                    navigationClicked = onNavigationClicked,
                    actionClicked = onActionClicked,
                    content = content,
                    bsvacTextStyle = bsvacTextStyle,
                    sectionIds = middleColumnSectionIds
                )
                StatusRow(
                    scope = this,
                    statusMessage = DataSource.StatusBarMessage(),
                    statusSymbol = DataSource.StatusBarSymbol(),
                    dateString = time,
                    bsvacTextStyle = bsvacTextStyle
                )
                BottomRow(
                    this,
                    mainUiState = mainUiState,
                    navigationClicked = onNavigationClicked,
                    actionClicked = onActionClicked,
                    bsvacTextStyle = bsvacTextStyle,
                    sectionIds = bottomRowSectionIds
                )
            }
            DevSettings(modifier = Modifier.align(Alignment.TopEnd),
                currentFontSize = currentFontSize,
                onFontSizeUpdated = onFontSizeUpdated
            )
        }
    }
}
@Composable
@Preview(widthDp = 900, heightDp = 600)
fun AppPreview(@PreviewParameter(SectionParameterProvider::class) sectionId : ContentSectionId,
               @PreviewParameter(provider = ErrorMessageProvider::class) displayError: String?){
    val logonSubScreenModel = LogonSubScreenModel(
        bsvacTextStyle = BsvacTextStyle(15.sp),
        choices = Choices(
            agencyOptions = AgencyModel.entries,
            boroughOptions = BoroughModel.entries,
            crewMemberOptions = CrewModel.entries,
            unitOptions = AmbulanceUnitModel.entries,
            tourOptions = TourModel.entries,
            radioOptions = RadioModel.entries,
        ),
        onSelection = OnSelection(
            onCrewMemberSelected = {_,_ -> },
            onAgencySelected = {},
            onBoroughSelected = {},
            onAmbulanceUnitSelected = {},
            onTourSelected = {},
            onRadioSelected = {_,_ -> },
        ),
        selected = Selected()
    )
    AppContent(
        mainUiState = MainUiState.Loaded(),
        onNavigationClicked = { },
        onActionClicked = { },
        subscreenDisplayed = sectionId,
        time = "Time",
        content = SubScreens.content(
            modifier = Modifier,
            sectionId = sectionId,
            displayError = displayError,
            logonSubScreenModel = logonSubScreenModel
        ),
        bsvacTextStyle = BsvacTextStyle(15.sp),
        currentFontSize = 15.sp,
        onFontSizeUpdated = {},
        topRowSectionIds = ContentSectionId.topRowIds(),
        middleColumnSectionIds = ContentSectionId.middleColumnIds(),
        bottomRowSectionIds = ContentSectionId.bottomRowIds(),
    )
}

class ErrorMessageProvider: PreviewParameterProvider<String?>{
    override val values: Sequence<String?>
        get() = DataSource.ErrorDialogPreviewParameters().asSequence()
    override val count: Int
        get() = values.count()


}

class SectionParameterProvider: PreviewParameterProvider<ContentSectionId> {
    override val values: Sequence<ContentSectionId>
        get() = ContentSectionId.entries.asSequence()
    override val count: Int
        get() = values.count()
}

